import { action, computed } from 'mobx';
import { getCountryAbbr, getCountryCodesFromSchema, trimCountryCode, addPlusSignForE164Pattern, formatPhoneIntoE164 } from '../../../utils/AddressFormUtils';
import { FIELD_TYPE, INPUT_TYPE } from '../../../constants/Constants';
import { ICountryCallingCodeConfig, IAddressSchema, AutoChangePhoneNumberPropertyType } from '../../../api/JsonSchemaApi';
import BaseAddressFormStore, { ISchemaCustomValidator, IBaseFormAttributes } from './BaseAddressFormStore';
import { IAddressAttributes } from '../Address/Address';
import { IPhoneCountryPattern } from '../../../domain/entities/Config';

export interface IAddressSchemaLoadingOptions {
  hasStates: boolean;
}

export interface ICustomValidator { 
  pattern: string;
  errorMessage: string;
}

export interface IAddressFormStoreOptions<T> {
  initialValues: T;
}

export interface IPhoneFormAttributes extends IBaseFormAttributes {
  phone?: string;
  [FIELD_TYPE.PHONE]?: string;
  [FIELD_TYPE.SECOND_PHONE]?: string;
}

// This enables a form store to handle phone validation using custom country codes patterns
// instead of traditional schema handling
class PhoneFormStore<T extends IPhoneFormAttributes> extends BaseAddressFormStore<T> {

  @computed get useE164PhoneStandard(): boolean {
    return this.config.useE164PhoneStandard;
  }

  @computed get phoneCountryPattern(): IPhoneCountryPattern {
    return this.config.phonePatterns;
  }

  // conditionally format phone into E164 to support legacy data
  formatPhone(value: string, countryId: number): string {
    return this.useE164PhoneStandard ? formatPhoneIntoE164(value, countryId) : value;
  }

  protected runPreValidationHandling(): void {
    this.setCustomPhoneValidations();
    this.updateCountryCallingPhonesByPattern();
  }

  private setCountryCallingCodeConfigValidations(field: keyof IAddressAttributes): void {
    const schemaField = this.addressSchema.properties[field];
    if (schemaField) {
      const countryCallingCodeConfig = this.getCountryCallingCodeConfig(schemaField.additionalProperties?.countryCodes);
  
      if (countryCallingCodeConfig) {
        const countryCallingCode = { ...countryCallingCodeConfig };

        if (this.useE164PhoneStandard) {
          // this is temporary, we want the global patterns to be updated to allow "+"
          countryCallingCode.pattern = addPlusSignForE164Pattern(countryCallingCodeConfig.pattern);
        }

        const validations = {
          customValidation: {
            countryCallingCodeConfig: countryCallingCode,
            errorMessage: '',
          },
        };
        this.setFieldValidations(field, validations);
      }
    }
  }

  protected getCustomValidation(fieldType: INPUT_TYPE, errorMessage: string, requiredMessage: string): ISchemaCustomValidator {
    let phoneValidations;

    if (fieldType === INPUT_TYPE.TEL && this.shouldApplyCustomPhoneValidation) {
      phoneValidations = {
        customValidation: {
          errorMessage,
          pattern: this.currentSchemaCountryCallingCode?.pattern,
        },
        required: requiredMessage,
      };
    }

    return phoneValidations;
  }

  @computed get currentSchemaCountryCallingCode(): ICountryCallingCodeConfig {
    const countryCodes = getCountryCodesFromSchema(this.addressSchema?.properties);
    const countryAbbr = getCountryAbbr(this.values[FIELD_TYPE.COUNTRY_ID]);
    const countryCallingCodeConfig = countryCodes?.find(item => item.country === countryAbbr);

    return countryCallingCodeConfig || this.phoneCountryPattern[countryAbbr];
  }

  @computed get shouldApplyCustomPhoneValidation(): boolean {
    return !!this.currentSchemaCountryCallingCode;
  }

  public computePhoneBasedOnSchema(): void {
    if (this.useE164PhoneStandard) return;

    this.autoChangePhoneNumberFeature();
    this.updateCountryCallingPhonesByPattern();
  }

  // auto changing based on schema's autochangePhoneNumber field
  private autoChangePhoneNumberFeature = (): void => {
    const { [FIELD_TYPE.PHONE]: mobileNumber } = this.values;
    if (mobileNumber) {
      const autochangePhoneNumberProperty = this.addressSchema?.properties
        ?.phone1?.additionalProperties?.autochangePhoneNumber;

      // If has autochangePhoneNumber config eg: Australia
      // then need to replace the phone number by the pattern and replacement
      if (autochangePhoneNumberProperty) {
        const {
          pattern, replacement,
        } = autochangePhoneNumberProperty as AutoChangePhoneNumberPropertyType;
        const newPhoneNumber = mobileNumber?.replace(RegExp(pattern), replacement);
        this.setFieldValue(FIELD_TYPE.PHONE, newPhoneNumber);
      }
    }
  };

  // updating the phone based on country's calling code schema values
  public updateCountryCallingPhonesByPattern(): void {
    if (this.currentSchemaCountryCallingCode && this.phoneData && !this.useE164PhoneStandard) {
      this.setFieldValue(
        this.phoneData.phoneName,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.getFormattedPhoneNumber(this.currentSchemaCountryCallingCode, this.phoneData.phoneValue) as any,
      );
    }
  }

  @computed
  get phoneData(): { phoneName: keyof IPhoneFormAttributes; phoneValue: string } | undefined {
    const phonePosibleValues = [{
      phoneName: FIELD_TYPE.PHONE as keyof IPhoneFormAttributes,
      phoneValue: this.values[FIELD_TYPE.PHONE],
    }, {
      phoneName: FIELD_TYPE.SECOND_PHONE as keyof IPhoneFormAttributes,
      phoneValue: this.values[FIELD_TYPE.SECOND_PHONE],
    }, {
      phoneName: 'phone' as keyof IPhoneFormAttributes,
      phoneValue: this.values.phone,
    },
    ];

    return phonePosibleValues.find(item => !!item.phoneValue);
  }

  private setCustomPhoneValidations(): void {
    this.setCountryCallingCodeConfigValidations('phone' as keyof IAddressAttributes);
    this.setCountryCallingCodeConfigValidations(FIELD_TYPE.PHONE);
    this.setCountryCallingCodeConfigValidations(FIELD_TYPE.SECOND_PHONE);
  }

  // Since phone type is not a user editable field just 
  // set the value on the form store by schema requirements
  private setPhoneNumberTypeValue(): void {
    const phoneTypes = [FIELD_TYPE.PHONE1_TYPE, FIELD_TYPE.PHONE2_TYPE];
    const phoneType = (Object.keys(this.addressSchema?.properties || {}) as Array<keyof IAddressAttributes>)
      .find(item => phoneTypes.includes(item) && this.addressSchema?.properties[item]?.required);

    const phoneTypePattern = this.addressSchema?.properties?.[phoneType]?.pattern;
    const phoneTypeValue = typeof phoneTypePattern === 'number'
      ? phoneTypePattern : 2;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.setFieldValue(phoneType as keyof IPhoneFormAttributes, phoneTypeValue as any);
  }

  public getCountryCallingCodeConfig(
    countryCodes: ICountryCallingCodeConfig[] = [],
  ): ICountryCallingCodeConfig {
    const countryAbbr = getCountryAbbr(this.values[FIELD_TYPE.COUNTRY_ID]);

    return countryCodes.find(item => item.country === countryAbbr);
  }

  public getFormattedPhoneNumber(
    countryCallingCodeConfig: ICountryCallingCodeConfig,
    originalPhoneNumber: string,
  ): string {
    const { countryCallingCode, formatter } = countryCallingCodeConfig;
    if (formatter) {
      let formattedPhoneNumber = originalPhoneNumber;

      const phoneDigits = originalPhoneNumber.replace(/\D/g, '');
      const placeHolders = {
        countryCallingCode: '::countryCallingCode::',
        mobileNumber: '::mobileNumber::',
      };
      const phoneWithoutCountryCode = trimCountryCode(phoneDigits, countryCallingCode);
      formattedPhoneNumber = formatter
        .replace(placeHolders.countryCallingCode, countryCallingCode)
        .replace(placeHolders.mobileNumber, phoneWithoutCountryCode);
    
      return formattedPhoneNumber;
    }

    return originalPhoneNumber;
  }

  @action
  public setAddressSchema(schema: IAddressSchema, options: IAddressSchemaLoadingOptions = {
    hasStates: false,
  }): void {
    super.setAddressSchema(schema, options);
    this.setPhoneNumberTypeValue();
  }
}

export default PhoneFormStore;
